/**
 * Tags de um cadastro — rótulos reutilizáveis para agrupar (ex.: a leva de uma importação). Busca o
 * catálogo global (com **criar na hora**), aplica ao cadastro e mostra como chips coloridos que se
 * removem no X. A mesma tag marca pessoas, empresas ou condomínios; depois a lista filtra por ela.
 */
import { Tag as TagIcon, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboReferencia } from '@/components/crud/ComboReferencia';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { estadoDaLista } from '@/components/ui/estado';
import { api, type TagCad } from '@/lib/api';
import type { OwnerTipo } from '@/lib/recursos/tipos';

const COR_PADRAO = '#6aa9ff';

export function PainelTags({ ownerTipo, ownerId }: { ownerTipo: OwnerTipo; ownerId: string }) {
  const base = `/cadastro/${ownerTipo}/${ownerId}/tags`;
  const [tags, setTags] = useState<TagCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [sel, setSel] = useState(''); // id no combo, resetado após aplicar

  async function carregar() {
    const r = await api.get<TagCad[]>(base);
    setCarregando(false);
    if (r.ok) setTags(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  async function aplicar(tagId: string) {
    if (!tagId) return;
    setSel('');
    if (tags.some((t) => t.id === tagId)) return; // já tem
    const r = await api.post(base, { tag_id: tagId });
    if (!r.ok) return void toast.error(r.erro);
    void carregar();
  }

  async function remover(tag: TagCad) {
    const r = await api.del(`${base}/${tag.id}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(`Tag "${tag.nome}" removida.`);
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TagIcon className="size-4 text-accent" />
          Tags
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Agrupe este cadastro (ex.: por lote de importação, projeto, origem). Depois a lista filtra
          por tag.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {estadoDaLista(carregando, tags.length === 0, 'Nenhuma tag ainda.') ?? (
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const cor = t.cor || COR_PADRAO;
              return (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px]"
                  style={{ borderColor: cor, color: cor, backgroundColor: `${cor}1a` }}
                >
                  {t.nome}
                  <button
                    type="button"
                    onClick={() => void remover(t)}
                    aria-label={`Remover ${t.nome}`}
                    className="rounded-full p-0.5 hover:bg-surface-hover"
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <div className="max-w-xs">
          <ComboReferencia
            id={`tags-${ownerId}`}
            valor={sel}
            onChange={aplicar}
            endpoint="/cadastro/tags"
            permitirCriar
          />
        </div>
      </CardContent>
    </Card>
  );
}
